import { Navigate, Outlet, NavLink, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { LayoutGrid, FolderOpen, Mail, Shield, LogOut, Star, Menu, Users, MessageSquare } from 'lucide-react';
import { useAdminAuth } from '@/context/AdminAuthContext';

export default function AdminLayout() {
  const { user, isAuthenticated, isLoading, isImpersonating, logout } = useAdminAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  // Build nav items based on role
  const allNavItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutGrid, roles: ['admin', 'sub-admin', 'super-admin'] },
    { path: '/admin/projects', label: 'Projects', icon: FolderOpen, roles: ['admin', 'sub-admin', 'super-admin'] },
    { path: '/admin/messages', label: 'Messages', icon: MessageSquare, roles: ['admin', 'super-admin'] },
    { path: '/admin/reviews', label: 'Reviews', icon: Star, roles: ['admin', 'super-admin'] },
    { path: '/admin/email', label: 'Email', icon: Mail, roles: ['editor', 'admin', 'sub-admin', 'super-admin'] },
    { path: '/admin/security', label: 'Security', icon: Shield, roles: ['editor', 'admin', 'sub-admin', 'super-admin'] },
    { path: '/admin/team', label: 'Our Team', icon: Users, roles: ['super-admin'] },
    { path: '/admin/users', label: 'Users', icon: Users, roles: ['super-admin'] },
  ];

  const navItems = allNavItems.filter(item => user && item.roles.includes(user.role));

  // Redirect editor from /admin to /admin/email
  if (location.pathname === '/admin' && user?.role === 'editor') {
    return <Navigate to="/admin/email" replace />;
  }

  // Block unauthorized routes
  const currentNav = allNavItems.find(n => {
    if (n.path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(n.path);
  });

  if (currentNav && user && !currentNav.roles.includes(user.role)) {
    // Redirect to first allowed page
    const firstAllowed = navItems[0];
    if (firstAllowed) {
      return <Navigate to={firstAllowed.path} replace />;
    }
  }

  const pageTitle = navItems.find(n => {
    if (n.path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(n.path);
  })?.label || 'Admin';

  const userInitial = user?.name?.charAt(0).toUpperCase() || 'A';

  return (
    <div className="min-h-screen bg-neutral-50 flex">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Admin Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#001A31] text-white flex flex-col transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="p-5 border-b border-white/10">
          <div className="flex items-center justify-center">
            <img 
              src="/logo_white.png" 
              alt="GeoENZ"
              className="h-12 w-auto object-contain"
            />
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/admin'}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 rounded-xl transition-colors text-sm ${
                  isActive
                    ? 'bg-white/10 text-white'
                    : 'text-white/50 hover:bg-white/5 hover:text-white'
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-white/10">
          <div className="px-3 py-2 mb-2">
            <p className="text-xs text-white/40">Signed in as</p>
            <p className="text-sm text-white/80 truncate">{user?.name}</p>
            <span className="text-[10px] px-1.5 py-0.5 bg-accent/20 text-accent rounded mt-1 inline-block capitalize">
              {user?.role}
            </span>
          </div>
          <button
            onClick={() => logout()}
            className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-white/50 hover:bg-red-500/10 hover:text-red-400 transition-colors text-sm"
          >
            <LogOut className="w-5 h-5" />
            {isImpersonating ? 'Exit account view' : 'Logout'}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 min-w-0">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-neutral-200 px-3 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 hover:bg-neutral-100 rounded-xl transition-colors"
            >
              <Menu className="w-5 h-5 text-neutral-600" />
            </button>
            <h1 className="text-base font-semibold text-primary">{pageTitle}</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-neutral-50 rounded-lg border border-neutral-100">
              <div className="w-2 h-2 bg-green-500 rounded-full" />
              <span className="text-xs text-neutral-500">Online</span>
            </div>
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white text-xs font-semibold">
              {userInitial}
            </div>
          </div>
        </header>

        {isImpersonating && (
          <div className="mx-3 mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 sm:mx-6">
            <span>Temporary 30 minute view of {user?.name}’s account. Changes apply to this account.</span>
            <button onClick={() => void logout()} className="font-semibold underline underline-offset-2">Exit account view</button>
          </div>
        )}

        {/* Page Content */}
        <div className="px-3 py-4 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
